import { type UseQueryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';

import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { useQueryStateAppContext } from '@/packages/query-state/shared/context/appContext';
import { type GetRealClustersResponse } from '@/packages/query-state/shared/types/clusters/ClustersResponse';
import { completeClustersJsonQuerySelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/smart';
import { mapConfigurationZoomSelector } from '@/packages/redux-state/store/slices/mapConfiguration/selectors/dumb';
import { mapConfigurationBboxSelector } from '@/packages/redux-state/store/slices/mapConfiguration/selectors/smart';
import { mapSelectedBboxSelector } from '@/packages/redux-state/store/slices/mapControls/selectors/smart';
import { getCancellationTokenFromSignal } from '@/packages/utils/getCancellationTokenFromSignal';

import { type IV2GetClustersForMapRequestSchema } from '@/repositories/generated/shared/search-offers-index/entities/get_clusters_for_map_v2_web/V2GetClustersForMapRequestSchema';

import { type QueryError, type QueryKey, getQueryKey } from './internal/config';
import { fetchGetClustersService } from './internal/fetchService';

type UseGetClustersQueryOptions<TData> = UseQueryOptions<GetRealClustersResponse, QueryError, TData, QueryKey>;

export function useGetClustersWithParametersQuery<TData = GetRealClustersResponse>(
  getParameters: () => IV2GetClustersForMapRequestSchema,
  options?: UseGetClustersQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const applicationContext = useQueryStateAppContext();

  return useQuery<GetRealClustersResponse, QueryError, TData, QueryKey>(
    getQueryKey(),
    ({ signal }) => {
      return fetchGetClustersService({
        applicationContext,
        cancellationToken: getCancellationTokenFromSignal(signal),
        parameters: getParameters(),
      });
    },
    { enabled: false, ...options },
  );
}

export function useGetClustersQuery<TData = GetRealClustersResponse>(
  options?: UseGetClustersQueryOptions<TData>,
): UseQueryResult<TData, QueryError> {
  const reduxState = useReduxState();

  return useGetClustersWithParametersQuery(() => {
    const state = reduxState.getState();

    const jsonQuery = completeClustersJsonQuerySelector(state);
    delete jsonQuery.bbox;

    const bbox = mapConfigurationBboxSelector(state);
    const zoom = mapConfigurationZoomSelector(state);
    const selectedBbox = mapSelectedBboxSelector(state);

    return { bbox, jsonQuery, selectedBbox, zoom };
  }, options);
}
